import { useMemo, useState } from 'react';

import path from 'path';

import {
	ContentCopy as ContentCopyIcon,
	DeleteOutlined as DeleteOutlinedIcon,
	EmailOutlined as EmailOutlinedIcon,
} from '@mui/icons-material';
import {
	Box,
	Button,
	Chip,
	MenuItem,
	Link as MuiLink,
	Portal,
	Stack,
	useTheme,
} from '@mui/material';
import {
	GridCallbackDetails,
	GridColDef,
	GridColumnVisibilityModel,
	GridPaginationModel,
	GridRenderCellParams,
	GridRowParams,
	GridSlotProps,
	GridSlots,
	GridSortModel,
	PropsFromSlot,
} from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Link as ReactRouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { DataGrid, DataGridToolbar, GridActionsCellItem, Select } from '~components';
import { pagePathSegments } from '~constants';
import { useAuthorize } from '~features/authentication';
import { datetimeNoSecondsFormatter } from '~utils/dateUtils';

import UserInvitation from '../../interfaces/userInvitation';
import UserInvitationsService from '../../services/userInvitationsService';

const toolbarIdString = 'topology-userinvitations-datagrid';

const service = new UserInvitationsService();

interface UserInvitationsDataGridProps {
	enableDelete?: boolean;
	onDelete?: (id: string) => void;
	onResendInvite?: (id: string) => void;
	onNewInvite?: () => void;
}

const UserInvitationsDataGrid = ({ onResendInvite, onNewInvite }: UserInvitationsDataGridProps) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const theme = useTheme();
	const { isSuperAdmin } = useAuthorize();

	const [fetchParameters, setFetchParameters] = useState<object>({});
	const [sortModel, setSortModel] = useState<GridSortModel>([
		{
			field: 'expiryDate',
			sort: 'desc',
		},
	]);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
		organisation: isSuperAdmin(),
	});

	const columns = useMemo<GridColDef<UserInvitation>[]>(() => {
		const copyToClipboard = (text: string) => {
			navigator.clipboard.writeText(text);
			enqueueSnackbar(t('views.tableResults.invitationsUsers.clipboard'));
		};

		return [
			{
				field: 'email',
				headerName: t('email'),
				renderCell: (params: GridRenderCellParams<UserInvitation>) => (
					<MuiLink href={`mailto:${params.value}`}>{params.value}</MuiLink>
				),
				flex: 1,
			},
			{
				field: 'expiryDate',
				headerName: t('expiryDate'),
				valueFormatter: (value: Date) => datetimeNoSecondsFormatter.format(value),
				cellClassName: (params) => (params.row.status === 'expired' ? 'error-text-color' : ''),
				flex: 1,
			},
			{
				field: 'organisation',
				headerName: t('organisation'),
				renderCell: (params) => (
					<MuiLink
						component={ReactRouterLink}
						to={`/${path.join(pagePathSegments.Organisations, params.value.id, pagePathSegments.Summary)}`}
					>
						{params.value.label}
					</MuiLink>
				),
				flex: 1,
			},
			{
				field: 'userGroup',
				headerName: t('userGroup'),
				renderCell: (params: GridRenderCellParams<UserInvitation>) =>
					!params.value ? '-' : (
						<MuiLink
							component={ReactRouterLink}
							to={`/${path.join(pagePathSegments.UserManagement, pagePathSegments.UserGroups, params.value.id, pagePathSegments.Summary)}`}
						>
							{params.value.label}
						</MuiLink>
					),
				flex: 1,
			},
			{
				field: 'culture',
				headerName: t('language'),
				valueFormatter: (value: string) => value ?? '-',
			},
			{
				field: 'status',
				headerName: t('status'),
				renderCell: (params: GridRenderCellParams<UserInvitation>) => (
					<Chip label={params.value} color={params.row.status === 'active' ? 'success' : 'error'} />
				),
			},
			{
				field: 'actions',
				type: 'actions',
				width: 80,
				align: 'right',
				resizable: false,
				hideable: false,
				getActions: (params: GridRowParams<UserInvitation>) => [
					<GridActionsCellItem
						key={`${params.id}-mail`}
						{...(params.row.status === 'active' ?
							{
								icon: <ContentCopyIcon />,
								label: t('view.usermanagement.invitations.button.contained.copyinvite'),
								onClick: () => copyToClipboard(params.row.link),
							}
						:	{
								icon: <EmailOutlinedIcon />,
								label: t('view.usermanagement.invitations.button.contained.resendlink'),
								onClick: () => onResendInvite?.(params.id.toString()),
							})}
					/>,
					<GridActionsCellItem
						key={`${params.id}-delete`}
						icon={<DeleteOutlinedIcon color='error' />}
						label={t('ui.delete')}
						showInMenu
						onClick={() => onResendInvite?.(params.id.toString())}
						sx={{ color: theme.palette.error.main }}
					/>,
				],
			},
		];
	}, [onResendInvite, t, theme, enqueueSnackbar]);

	const { data, isLoading, isValidating, error } = useSWR(
		fetchParameters ? [service.basePath, fetchParameters] : null,
		([_, args]) => service.getUserInvitations(args),
		{
			onSuccess: (res) => setTotalCount(res.total),
			keepPreviousData: true,
		}
	);

	const [totalCount, setTotalCount] = useState<number>(data?.total || 0);

	const handlePaginationChange = (value: GridPaginationModel, details: GridCallbackDetails) => {
		if (details.reason === 'setPaginationModel' && (isLoading || isValidating)) {
			service.abortCurrentRequest('pagination change');
		}

		setFetchParameters((prev) => ({
			...prev,
			page: value.page + 1,
			pageSize: value.pageSize,
		}));
	};

	return (
		<Stack
			spacing={2}
			sx={{
				height: 1,
			}}
		>
			<Box id={toolbarIdString} />
			<DataGrid
				columns={columns}
				error={error}
				loading={isLoading || isValidating}
				rows={data?.results ?? []}
				rowCount={totalCount}
				onPaginationModelChange={handlePaginationChange}
				sortModel={sortModel}
				onSortModelChange={(newModel) => setSortModel(newModel)}
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
				slots={{
					toolbar: Toolbar as GridSlots['toolbar'],
				}}
				slotProps={{
					toolbar: {
						onNewInvite: onNewInvite,
					} as GridSlotProps['toolbar'],
				}}
				sx={{
					flexGrow: 1,
					flexBasis: 200,
					'& .error-text-color': {
						color: theme.palette.error.main,
					},
				}}
			/>
		</Stack>
	);
};

interface ToolbarProps extends PropsFromSlot<GridSlots['toolbar']> {
	onNewInvite?: () => void;
}

const Toolbar = ({ onNewInvite }: ToolbarProps) => {
	const { t } = useTranslation();

	return (
		<Portal container={() => document.getElementById(toolbarIdString)}>
			<DataGridToolbar
				filters={
					<Select size='small'>
						{['active', 'expired'].map((el) => (
							<MenuItem key={el} value={el}>
								{el}
							</MenuItem>
						))}
					</Select>
				}
			>
				<Button variant='contained' onClick={() => onNewInvite?.()}>
					{t('ui.button.contained.inviteusers')}
				</Button>
			</DataGridToolbar>
		</Portal>
	);
};

export default UserInvitationsDataGrid;
